import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, Chip, Button, Divider, List, ListItem, ListItemText, CircularProgress, LinearProgress, Alert, Tooltip } from '@mui/material';
import { ArrowBack, FolderOpen, CalendarToday, AttachMoney, Business, AutoAwesome } from '@mui/icons-material';
import api from '../../services/api';

export default function LicitacionDetailPage() {
  const { codigo } = useParams();
  const [licitacion, setLicitacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // --- Estado del Filtro IA (OpenCode) para la vista Detalle/top ---
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState('');
  const [aiInfo, setAiInfo] = useState(null);
  const [aiError, setAiError] = useState('');
  const [prevItems, setPrevItems] = useState(null);

  useEffect(() => {
    if (codigo === 'top') {
      api.get('/licitaciones/top?limit=20').then(r => {
        setLicitacion({ items: r.data.items || r.data || [], isTop: true });
        setLoading(false);
      }).catch(() => setLoading(false));
      return;
    }
    api.get(`/licitaciones/${codigo}`).then(r => {
      setLicitacion(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [codigo]);

  const handleAIFilterTop = async () => {
    if (!licitacion || !licitacion.isTop || aiLoading) return;
    const itemsActuales = licitacion.items || [];
    if (itemsActuales.length === 0) return;
    setAiLoading(true);
    setAiError('');
    setAiInfo(null);
    setPrevItems(itemsActuales);
    const lotes = Math.max(Math.ceil(itemsActuales.length / 12), 1);
    let ticks = 0;
    const ticker = setInterval(() => {
      ticks += 1;
      const loteActual = Math.min(Math.ceil((ticks * 12) / itemsActuales.length), lotes);
      setAiProgress('OpenCode evaluando lote ' + loteActual + ' de ' + lotes + ' (' + itemsActuales.length + ' licitaciones, modelo big-pickle via Tor)...');
    }, 8000);
    try {
      const datos = itemsActuales.map(l => ({
        Codigo: l.codigo,
        Nombre: l.nombre,
        Organismo: l.organismo || '',
        Descripcion: l.descripcion || '',
        fecha_cierre: l.fecha_cierre || null,
        monto_estimado: l.monto_estimado || null,
        categoria: l.categoria || null,
      }));
      const res = await api.post('/licitaciones/filtrar-opencode', {
        datos_entrada: datos,
        umbral: 70,
        batch_size: 12,
        timeout_lote: 150,
      }, { timeout: 900000 });
      const data = res.data || {};
      if (data.status === 'success') {
        // Merge: preserva todos los atributos descriptivos de la fila original
        // y normaliza claves Capitalizadas del endpoint a la forma canonica minuscula.
        const baseById = {};
        itemsActuales.forEach(r0 => {
          if (r0 && r0.codigo !== undefined && r0.codigo !== null) baseById[String(r0.codigo)] = r0;
        });
        const merged = (data.items || []).map(it => {
          const key = String(it.Codigo !== undefined && it.Codigo !== null ? it.Codigo : (it.codigo !== undefined && it.codigo !== null ? it.codigo : it.id));
          const base = baseById[key] || {};
          const out = { ...base, ...it };
          out.codigo = base.codigo !== undefined ? base.codigo : (it.Codigo !== undefined ? it.Codigo : it.codigo);
          out.nombre = base.nombre !== undefined ? base.nombre : (it.Nombre !== undefined ? it.Nombre : it.nombre);
          out.organismo = base.organismo !== undefined ? base.organismo : (it.Organismo !== undefined ? it.Organismo : it.organismo);
          return out;
        });
        // El backend ya ordena por ai_score desc: la tabla queda reorganizada con el top mas relevante primero.
        setLicitacion(prev => ({ ...prev, items: merged }));
        const r = data.resumen || {};
        setAiInfo('Filtro IA (OpenCode/' + (r.modelo || 'big-pickle') + '): ' + data.total_coincidentes + ' de ' + data.total_procesados + ' licitaciones pertinentes al perfil de negocio TI (umbral ' + (r.umbral_score !== undefined ? r.umbral_score : 70) + ')'
          + (r.descartadas !== undefined ? ' \u00b7 descartadas ' + r.descartadas : '')
          + (r.errores_evaluacion ? ' \u00b7 errores de evaluaci\u00f3n: ' + r.errores_evaluacion : ''));
      } else {
        setAiError('Respuesta inesperada del filtro IA');
      }
    } catch (err) {
      console.error('Error filtro IA:', err);
      setAiError((err.response && err.response.data && err.response.data.detail) || err.message || 'Error en el filtro IA');
    } finally {
      clearInterval(ticker);
      setAiLoading(false);
      setAiProgress('');
    }
  };

  const handleRestoreTop = () => {
    if (prevItems) {
      setLicitacion(prev => ({ ...prev, items: prevItems }));
      setAiInfo(null);
      setPrevItems(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!licitacion) return <Typography>No se encontró la licitación</Typography>;

  if (licitacion.isTop) {
    const itemsTop = licitacion.items || [];
    const mostrarIA = itemsTop.some(l => l.ai_score !== undefined);
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Volver</Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Top Licitaciones Más Interesantes</Typography>
          <Button variant="outlined" startIcon={<AutoAwesome />} onClick={handleAIFilterTop}
            disabled={aiLoading || loading} sx={{ color: '#7B1FA2', borderColor: '#7B1FA2' }}>
            {aiLoading ? 'Evaluando con IA...' : 'Filtro IA (OpenCode)'}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Criterio: menor monto + tipo de trabajo de baja complejidad IT
        </Typography>

        {aiLoading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="indeterminate" sx={{
              height: 6, borderRadius: 3,
              '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #7B1FA2, #E040FB)' }
            }} />
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {aiProgress || 'Conectando con el motor OpenCode...'}
            </Typography>
          </Box>
        )}
        {aiError && <Alert severity="error" sx={{ mb: 2 }}>{aiError}</Alert>}
        {aiInfo && !aiLoading && (
          <Alert severity="success" sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" onClick={handleRestoreTop}>Ver todos</Button>}>
            {aiInfo}
          </Alert>
        )}

        <Card>
          <CardContent>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #0047AB' }}>Código</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #0047AB' }}>Nombre</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #0047AB' }}>Organismo</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #0047AB' }}>Categoría</th>
                  <th style={{ textAlign: 'right', padding: 8, borderBottom: '2px solid #0047AB' }}>Monto</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #0047AB' }}>Cierre</th>
                  {mostrarIA && <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #7B1FA2' }}>Evaluación IA</th>}
                </tr>
              </thead>
              <tbody>
                {itemsTop.map((l, i) => (
                  <tr key={l.codigo} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/licitaciones/${l.codigo}`)}>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{i + 1}. {l.codigo}</td>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{l.nombre?.substring(0, 60)}</td>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{l.organismo || '-'}</td>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee' }}><Chip label={l.categoria || 'N/A'} size="small" /></td>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      {l.monto_estimado ? `$${Number(l.monto_estimado).toLocaleString('es-CL')}` : '-'}
                    </td>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                      {l.fecha_cierre ? new Date(l.fecha_cierre).toLocaleDateString('es-CL') : '-'}
                    </td>
                    {mostrarIA && (
                      <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                        {l.ai_score !== undefined ? (
                          <Tooltip title={l.ai_justificacion || 'Sin justificación'} arrow placement="top">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip size="small" color={l.ai_score >= 90 ? 'success' : 'primary'} label={'IA ' + l.ai_score} />
                              <Typography variant="caption" sx={{ maxWidth: 140 }} noWrap>{l.ai_categoria}</Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Volver</Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip label={licitacion.codigo} color="primary" sx={{ fontWeight: 700 }} />
            <Chip label={licitacion.categoria || 'N/A'} variant="outlined" />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{licitacion.nombre}</Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Organismo</Typography>
                  <Typography variant="body2" fontWeight={500}>{licitacion.organismo || 'N/A'}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Monto Estimado</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {licitacion.monto_estimado ? `$${Number(licitacion.monto_estimado).toLocaleString('es-CL')}` : 'No especificado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Fecha Cierre</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {licitacion.fecha_cierre ? new Date(licitacion.fecha_cierre).toLocaleDateString('es-CL') : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {licitacion.descripcion && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Descripción</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{licitacion.descripcion}</Typography>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Button variant="contained" startIcon={<FolderOpen />}
            onClick={() => navigate('/documentos', { state: { createFromLicitacion: licitacion } })}>
            Agregar a Documentos
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
